import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { employeeAPI, alertsAPI } from '../services/api';
import { FEELINGS, ALINEA_PROFILES, TEAM_FEELING, BOSS_PERCEPTION, DOMINANT_PHRASE } from '../utils/constants';
import { buildWellbeingDays, getWellbeingDaySummary } from '../utils/wellbeingDaySummary.js';
import { surveyDateAtNoon } from '../utils/surveyDate.js';
import './TrabajadorDetalle.css';

/** Adapta GET /employees/:id al shape que usa esta pantalla. */
function mapEmployeeApiToDetalle(raw) {
    if (!raw) return null;
    const daily = raw.dailySurveys || [];
    const wellbeingDays = buildWellbeingDays(daily).slice(0, 7);

    const weekly = [...(raw.weeklySurveys || [])].sort((a, b) =>
        String(b.week_start).localeCompare(String(a.week_start))
    );
    const w0 = weekly[0];
    const ultimaEncuesta = w0
        ? {
              relacion_jefe: w0.boss_perception,
              clima_equipo: w0.team_feeling,
              frase_dominante: w0.dominant_phrase,
          }
        : null;

    const alertas = (raw.alerts || [])
        .filter((a) => a.status === 'active')
        .map((a) => ({
            id: a.id,
            codigo_regla: a.alert_code,
            nivel: a.level,
            title: a.title,
            description: a.description,
        }));

    return {
        perfil: {
            nombre_completo: raw.full_name,
            area_nombre: raw.area_name,
            puesto: raw.position || '—',
            perfil_alinea: raw.alinea_profile || 'constante',
        },
        wellbeingDays,
        ultimaEncuesta,
        alertas,
    };
}

export default function TrabajadorDetalle() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showEPA, setShowEPA] = useState(false);
    const [intervention, setIntervention] = useState('');
    const [notes, setNotes] = useState('');
    const [selectedDayIso, setSelectedDayIso] = useState(null);

    useEffect(() => {
        fetchWorkerData();
        const savedNotes = localStorage.getItem(`notes_worker_${id}`);
        if (savedNotes) setNotes(savedNotes);
    }, [id]);

    const fetchWorkerData = async () => {
        try {
            const res = await employeeAPI.getDetalle(id);
            const payload = res.data?.data ?? res.data;
            setData(mapEmployeeApiToDetalle(payload));
            setSelectedDayIso(null);
        } catch (err) {
            console.error('Error fetching worker details:', err);
        } finally {
            setLoading(false);
        }
    };

    const daySummary = useMemo(() => {
        if (!selectedDayIso || !data?.wellbeingDays) return null;
        const day = data.wellbeingDays.find((d) => d.dateIso === selectedDayIso);
        return getWellbeingDaySummary(day);
    }, [selectedDayIso, data]);

    const saveNotes = (val) => {
        setNotes(val);
        localStorage.setItem(`notes_worker_${id}`, val);
    };

    const handleIntervention = async () => {
        if (!intervention) return alert('Por favor escribe el acuerdo.');
        try {
            const alertId = data.alertas[0]?.id;
            if (alertId) {
                await alertsAPI.intervene(alertId, {
                    actionTaken: intervention,
                    outcome: null,
                });
            }
            alert('¡Intervención registrada con éxito!');
            setShowEPA(false);
            setIntervention('');
            fetchWorkerData();
        } catch (err) {
            alert('Error al guardar intervención');
        }
    };

    if (loading) return <div className="loading">Cargando perfil...</div>;
    if (!data) return <div className="error">No se encontró el trabajador.</div>;

    const { perfil, wellbeingDays, ultimaEncuesta, alertas } = data;
    const profileInfo = ALINEA_PROFILES[perfil.perfil_alinea?.toLowerCase()] || ALINEA_PROFILES.constante;
    const historyDisplay = [...wellbeingDays].reverse();
    const isAdminUser = user?.rol === 'admin' || user?.rol === 'superadmin';

    return (
        <div className="worker-detail">
            <header className="detail-header">
                {!isAdminUser && (
                    <button
                        className="btn-back"
                        onClick={() => navigate(user?.rol === 'supervisor' ? '/' : -1)}
                    >
                        ← Volver
                    </button>
                )}
                <div className="worker-main-info">
                    <h1>{perfil.nombre_completo}</h1>
                    <p className="area-info">
                        {perfil.area_nombre} · {perfil.puesto}
                    </p>
                </div>
                <div className="profile-badge">
                    <span className="badge-emoji">{profileInfo.emoji}</span>
                    <span className="badge-name">{profileInfo.name}</span>
                </div>
            </header>

            <section className="detail-section glass-card">
                <h3>Historial de bienestar (7 días)</h3>
                <p className="history-hint">Toca un día para ver el resumen de ese registro (llegada y cierre).</p>
                <div className="history-row">
                    {historyDisplay.length === 0 ? (
                        <p className="text-muted">Sin registros recientes de jornada.</p>
                    ) : (
                        historyDisplay.map((day) => {
                            const mood = day.start?.feeling;
                            const isOn = selectedDayIso === day.dateIso;
                            return (
                                <button
                                    key={day.dateIso}
                                    type="button"
                                    className={`history-day${isOn ? ' history-day-on' : ''}`}
                                    onClick={() =>
                                        setSelectedDayIso((prev) =>
                                            prev === day.dateIso ? null : day.dateIso
                                        )
                                    }
                                    aria-pressed={isOn}
                                >
                                    <span className="emoji" aria-hidden>
                                        {FEELINGS[mood]?.emoji || '·'}
                                    </span>
                                    <span className="day-name">
                                        {surveyDateAtNoon(day.dateIso)?.toLocaleDateString('es-ES', {
                                            weekday: 'short',
                                            day: 'numeric',
                                        }) ?? '—'}
                                    </span>
                                </button>
                            );
                        })
                    )}
                </div>

                {daySummary && (
                    <div className="day-summary-panel" role="region" aria-label="Resumen del día">
                        <p className="day-summary-kicker">Resumen del día</p>
                        <h4 className="day-summary-title">
                            <span aria-hidden>{daySummary.emoji}</span> {daySummary.dateLong}
                        </h4>
                        <ul className="day-summary-list">
                            {daySummary.lines.map((line, i) => (
                                <li key={i}>
                                    <span className="day-summary-label">{line.label}</span>
                                    <span className="day-summary-value">{line.value}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </section>

            <section className="detail-section grid-2">
                <div className="glass-card survey-summary">
                    <h3>Última encuesta semanal</h3>
                    {ultimaEncuesta ? (
                        <ul className="survey-list">
                            <li>
                                <strong>Relación jefe:</strong>
                                <span>
                                    {BOSS_PERCEPTION[ultimaEncuesta.relacion_jefe]?.emoji}{' '}
                                    {BOSS_PERCEPTION[ultimaEncuesta.relacion_jefe]?.label}
                                </span>
                            </li>
                            <li>
                                <strong>Relación equipo:</strong>
                                <span>
                                    {TEAM_FEELING[ultimaEncuesta.clima_equipo]?.emoji}{' '}
                                    {TEAM_FEELING[ultimaEncuesta.clima_equipo]?.label}
                                </span>
                            </li>
                            <li>
                                <strong>Frase dominante:</strong>
                                <span>
                                    {DOMINANT_PHRASE[ultimaEncuesta.frase_dominante]?.emoji}{' '}
                                    {DOMINANT_PHRASE[ultimaEncuesta.frase_dominante]?.label}
                                </span>
                            </li>
                        </ul>
                    ) : (
                        <p className="text-muted">No hay encuestas recientes.</p>
                    )}
                </div>

                <div className="glass-card active-alerts-card">
                    <h3>Alertas activas</h3>
                    {alertas.length > 0 ? (
                        <div className="alert-list-mini">
                            {alertas.map((a) => (
                                <div key={a.id} className={`alert-tag level-${a.nivel}`}>
                                    ⚠️ {a.codigo_regla}: {a.nivel.toUpperCase()}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-muted">Sin alertas activas.</p>
                    )}
                    <button className="btn-epa-action" onClick={() => setShowEPA(true)}>
                        Registrar intervención EPA
                    </button>
                </div>
            </section>

            <section className="detail-section">
                <div className="glass-card notes-card">
                    <h3>Mis notas privadas (solo tú las ves)</h3>
                    <textarea
                        value={notes}
                        onChange={(e) => saveNotes(e.target.value)}
                        placeholder="Escribe aquí observaciones, temas a tratar o compromisos..."
                    />
                </div>
            </section>

            {showEPA && (
                <div className="modal-overlay">
                    <div className="modal-content animate-in">
                        <h2>Guía de intervención EPA</h2>
                        <p className="modal-sub">Escucha · Procesa · Ajusta</p>

                        <div className="epa-steps">
                            <div className="step">
                                <strong>E</strong>scuchar sin juzgar el motivo del estado de ánimo.
                            </div>
                            <div className="step">
                                <strong>P</strong>erdonar el error o la falta de enfoque momentánea.
                            </div>
                            <div className="step">
                                <strong>A</strong>daptar la tarea o el apoyo para recuperar el ritmo.
                            </div>
                        </div>

                        <label>¿Qué se acordó o cómo se apoyó al trabajador?</label>
                        <textarea
                            value={intervention}
                            onChange={(e) => setIntervention(e.target.value)}
                            placeholder="Ej: Se le asignó una tarea más ligera hoy por problemas personales..."
                        />

                        <div className="modal-actions">
                            <button className="btn-cancel" onClick={() => setShowEPA(false)}>
                                Cancelar
                            </button>
                            <button className="btn-save" onClick={handleIntervention}>
                                Guardar intervención
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
